<template>
  <standard-dialog
    v-model="model"
    :title="restaurantName"
    :subtitle="dialogSubtitle"
    icon="mdi-cash-multiple"
    :max-width="600"
    hide-actions
  >
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <template v-else>
      <v-alert v-if="!canEdit" type="info" text dense class="mb-4">
        {{ $t("services_read_only_notice") }}
      </v-alert>

      <v-btn v-if="canEdit" color="primary" depressed class="mb-4" @click="setPriceDialog = true">
        <v-icon left small>mdi-plus</v-icon>{{ $t("set_new_price") }}
      </v-btn>

      <div v-if="!history.length" class="text-center grey--text py-6">
        {{ $t("price_not_entered") }}
      </div>

      <v-card
        v-for="entry in history"
        :key="entry.id"
        outlined
        class="mb-2 pa-3"
        :class="{ 'pricing-entry--archived': !entry.is_current }"
      >
        <v-row no-gutters align="center">
          <div>
            <span :class="entry.is_current ? 'text-h6' : 'body-1'">{{ entry.opis }}</span>
            <v-chip v-if="entry.is_current" x-small color="success" text-color="white" class="ml-2">
              {{ $t("current_price") }}
            </v-chip>
            <v-icon v-if="!entry.is_verified" small color="warning" class="ml-1">mdi-alert</v-icon>
            <div class="caption grey--text">
              {{ localeDate(entry.valid_from) }} — {{ entry.valid_to ? localeDate(entry.valid_to) : $t("price_open_ended") }}
            </div>
            <div class="caption grey--text">
              {{ $t("created_by") }}: {{ entry.created_by || "—" }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            v-if="!entry.is_verified && canEdit"
            small
            outlined
            color="warning"
            @click="verify(entry)"
            :loading="verifyingId === entry.id"
          >
            <v-icon left small>mdi-check</v-icon>{{ $t("confirm_price") }}
          </v-btn>
        </v-row>
      </v-card>

      <v-row no-gutters justify="end" class="mt-4">
        <v-btn text @click="close">{{ $t("cancel") }}</v-btn>
      </v-row>
    </template>

    <set-price-dialog
      v-model="setPriceDialog"
      :restaurant-id="restaurant.id"
      :service-id="serviceId"
      :paying-types="payingTypeOptions"
      :current-price="currentPrice"
      @saved="onPriceSaved"
    />
  </standard-dialog>
</template>

<script>
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import SetPriceDialog from "./SetPriceDialog.vue";
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import pricingService from "@/services/pricing";

export default {
  name: "RestaurantPricingDialog",
  mixins: [hasModel, hasSnackbar],
  components: {
    StandardDialog,
    SetPriceDialog,
  },
  props: {
    restaurant: {
      type: Object,
      default: () => ({}),
    },
    serviceId: {
      type: [Number, String],
      required: true,
    },
    serviceName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      canEdit: true,
      history: [],
      options: {},
      setPriceDialog: false,
      verifyingId: null,
    };
  },
  watch: {
    model(val) {
      if (val) {
        this.fetchHistory();
      } else {
        this.history = [];
      }
    },
  },
  computed: {
    restaurantName() {
      return this.restaurant?.name || "";
    },
    dialogSubtitle() {
      return this.serviceName ? `${this.$t("price_list")} · ${this.serviceName}` : this.$t("price_list");
    },
    payingTypeOptions() {
      return Object.entries(this.options.paying_types || {}).map(([value, text]) => ({
        value: Number(value),
        text,
      }));
    },
    currentPrice() {
      return this.history.find((h) => h.is_current) || null;
    },
  },
  methods: {
    async fetchHistory() {
      this.loading = true;
      try {
        const { data } = await pricingService.getRestaurantPricing(this.restaurant.id, {
          service_id: this.serviceId,
        });
        this.canEdit = data.can_edit !== false;
        this.options = data.options || {};
        this.history = (data.history || [])
          .slice()
          .sort((a, b) => {
            if (a.is_current !== b.is_current) return a.is_current ? -1 : 1;
            return (b.valid_from || "").localeCompare(a.valid_from || "");
          });
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_price_history"));
        this.model = false;
      } finally {
        this.loading = false;
      }
    },
    async verify(entry) {
      this.verifyingId = entry.id;
      try {
        await pricingService.verifyRestaurantPrice(this.restaurant.id, entry.id);
        entry.is_verified = true;
        this.showSuccessSnackbar(this.$t("price_confirmed_successfully"));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_confirming_price"));
      } finally {
        this.verifyingId = null;
      }
    },
    onPriceSaved() {
      this.fetchHistory();
      this.$emit("priceChanged");
    },
    close() {
      this.model = false;
    },
  },
};
</script>

<style scoped>
.pricing-entry--archived {
  opacity: 0.75;
}
</style>
